import parsePhoneNumber from 'libphonenumber-js/max'
import Papa from 'papaparse'
import { ERRORS, STEPS } from './constants'
import { parse, isValid, format } from 'date-fns'
import ISO6391 from 'iso-639-1'
import countries from 'i18n-iso-countries'
import enLocale from 'i18n-iso-countries/langs/en.json'
countries.registerLocale(enLocale)

const MATCHING_COLUMNS = [
  { field: 'external_id', label: 'Membership Number' },
  { field: 'email', label: 'Email' },
  { field: 'phone_number', label: 'Phone Number' },
]

const getCsvSample = ({ type, ref }, length) => {
  return new Promise((resolve, reject) => {
    let rowN = 0
    let rows = { data: [] }
    Papa.parse(ref, {
      download: type === 'url',
      step: function (row, parser) {
        rows.data.push(row.data)
        if (rowN === length) parser.abort()
        rowN += 1
      },
      complete: () => {
        resolve(rows)
      },
      error: (err) => {
        reject(err)
      },
    })
  })
}

const isValidDate = (value, dateFormat) => {
  const parsedDate = parse(value, dateFormat, new Date())
  return isValid(parsedDate) && format(parsedDate, dateFormat) === value
}

const validateCsv = ({ type, csv, schema, confirmedColumns }) => {
  return new Promise((resolve, reject) => {
    /* Check data meets schema */
    let rowN = 0
    const errorRows = []
    const validRows = []
    let indexedSchema
    let abortMessage

    Papa.parse(csv.ref, {
      download: csv.type === 'url',
      worker: false,
      step: function (row, parser) {
        /* Check for end of file */
        if (!row.data.join()) {
          parser.abort()
          return
        }
        if (rowN === 0) {
          /* Check for required columns in header row */
          const fieldNames = schema.map((f) => f.name.trim().toLowerCase())
          let missing
          if (type === 'upload-list') {
            missing = confirmedColumns.filter(
              (field) => !row.data.some((f) => f.trim().toLowerCase() === field.trim().toLowerCase())
            )
          } else {
            missing = schema
              .filter(
                (field) =>
                  field.required === true &&
                  !row.data.some(
                    (f) => f.trim().toLowerCase() === field.name.trim().toLowerCase()
                  )
              )
              .map((field) => field.name)
          }

          const additional = row.data
            .filter((header) => !_.includes(fieldNames, header.trim().toLowerCase()))
            .map((header) => (!header.trim() ? '(empty column)' : header))

          let invalidColumns = []
          if (type === 'upload-list') {
            invalidColumns = row.data
            .filter((header) => header.trim().toLowerCase() !== confirmedColumns[0].trim().toLowerCase())
          }

          const issues = []
          if (missing?.length) {
            issues.push(`${ERRORS.CSV_COLUMNS_MISSING} [${missing.join(', ')}]`)
          }
          if (additional?.length && type !== 'upload-list') {
            issues.push(ERRORS.CSV_CUSTOM_COLUMNS.replace('{columns}', additional.join(', ')))
          }
          if (invalidColumns?.length) {
            issues.push(ERRORS.CSV_COLUMNS_LIST_INVALID)
          }

          if (issues.length) {
            abortMessage = issues.join('; ')
            parser.abort()
          } else {
            indexedSchema = schema.map((item) => {
              return {
                ...item,
                dataIndex: row.data.findIndex(
                  (f) => f.trim().toLowerCase() == item.name.toLowerCase()
                ),
              }
            })
          }
        }
        /* Validate each row */
        if (rowN > 0) {
          const errorCells = []
          let valid = true

          indexedSchema.forEach((item) => {
            if (type === 'upload-list' && item.name !== confirmedColumns[0]) {
              return
            }
            const result = validateField(item, row.data[item.dataIndex])
            if (!result) {
              errorCells.push(item.dataIndex)
              valid = false
            }
          })

          if (valid) {
            validRows.push({ index: rowN, data: row.data })
          } else {
            errorRows.push({ index: rowN, data: row.data, errorCells })
          }
        }
        /* emit progress updates */
        if (rowN % 100 === 0) updateProgress(row.meta.cursor, csv.ref.size)
        rowN += 1
      },
      complete: () => {
        if (abortMessage) reject({ reason: abortMessage })
        else resolve({ perfect: !errorRows.length, errorRows, validRows })
      },
      error: (err) => {
        reject(err)
      },
    })
  })
}

const updateProgress = (n, N) => {
  if (n % 10 === 0) {
    const event = new ProgressEvent('csvProcessing', {
      lengthComputable: true,
      loaded: n,
      total: N,
    })
    window.dispatchEvent(event)
  }
}

const validateField = (schemaItem, value) => {
  switch (schemaItem.type) {
    case 'string':
    case 'name':
      if (!value && !schemaItem.required) {
        return true
      }
      return typeof value === 'string' && !!value.length
    case 'date':
      if (!value) {
        return !schemaItem.required
      }
      if (typeof value === 'string') {
        const formats = ["yyyy-MM-dd", "dd-MM-yyyy"]
        return formats.some(format => isValidDate(value, format))
      }
      return false
    case 'phone': {
      if (!schemaItem.required && (!value || !value.trim())) return true

      const country = String(value)[0] === '+' ? '' : window.ENV.COUNTRY.toUpperCase()
      const parsed = parsePhoneNumber(value, country)

      if (parsed) {
        const allowedTypes = ["FIXED_LINE", "MOBILE", "VOIP", "PAGER", "FIXED_LINE_OR_MOBILE"];
        const phoneType = parsed.getType();
        const isValidType = phoneType ? allowedTypes.includes(phoneType) : false;

        return parsed && parsed.isValid() && isValidType;
      } else {
        return false
      }
    }
    case 'country': {
      if (!schemaItem.required && (!value || !value.trim())) return true

      try {
        return countries.isValid(value.toUpperCase())
      } catch {
        return false
      }
    }
    case 'language': {
      if (!schemaItem.required && (!value || !value.trim())) return true

      try {
        return ISO6391.validate(value.toLowerCase())
      } catch {
        return false
      }
    }
    default:
      if (!schemaItem.required && !value) {
        return true
      }
      return value && schemaItem.regex && value.match(schemaItem.regex)
  }
}

function stepIdx(identifier, steps = STEPS) {
  return steps.findIndex((obj) => obj.id === identifier)
}

function getCurrentStep(identifier, steps = STEPS) {
  const idx = stepIdx(identifier, steps)
  const current = steps[idx]
  return `Step ${idx + 1}: ${current.label}`
}

function getStep(identifier, operator = '+', steps = STEPS) {
  const idx = stepIdx(identifier, steps)
  const [other, prefix] =
    operator === '+' ? [steps[idx + 1], 'Next'] : [steps[idx - 1], 'Previous']
  return other ? `${prefix}: ${other.label}` : null
}

function nextStep(identifier, steps = STEPS) {
  return getStep(identifier, '+', steps)
}

function prevStep(identifier, steps = STEPS) {
  return getStep(identifier, '-', steps)
}

export {
  validateCsv,
  getCsvSample,
  getCurrentStep,
  nextStep,
  prevStep,
  MATCHING_COLUMNS,
}
